import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, theme } from 'antd';
import { Field } from '../../components/Field';

interface SubscriptionDto {
  id: string;
  userId: string;
  planId: string;
  // NOTE: this local shape shadows @wellverse/shared-ts's SubscriptionDto.
  billingCycle?: string;
  plan?: {
    name: string;
    planType: string;
    priceMonthlyCents: number;
    currency: string;
  } | null;
  status: string;
  provider: string;
  providerCustomerId?: string | null;
  providerSubId?: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEnd?: string | null;
  canceledAt?: string | null;
  expiresAt?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const statusColors: Record<string, string> = {
  ACTIVE: '#3ecf8e',
  TRIALING: '#3b82f6',
  PAST_DUE: '#f59e0b',
  CANCELED: '#898989',
  EXPIRED: '#ef4444',
  PAUSED: '#8b5cf6',
};

const providerColors: Record<string, string> = {
  STRIPE: '#635bff',
  REVENUECAT: '#e04f7a',
  APPLE: '#000000',
  GOOGLE: '#34a853',
  MANUAL: '#898989',
};

export const SubscriptionShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<SubscriptionDto>({ resource: 'subscriptions' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="subscriptions">
      <div style={{ padding: '8px 0' }}>
        <Field label="ID">{item.id}</Field>
        <Field label="User ID">{item.userId}</Field>
        <Field label="Plan ID">{item.planId}</Field>
        {item.plan && (
          <>
            <Field label="Plan Name">{item.plan.name}</Field>
            <Field label="Plan Type">
              <Tag style={tagStyle('#8b5cf6')}>{item.plan.planType}</Tag>
            </Field>
            <Field label="Plan Price">{`$${(item.plan.priceMonthlyCents / 100).toFixed(2)} ${item.plan.currency}/mo`}</Field>
          </>
        )}
        <Field label="Status">
          <Tag style={tagStyle(statusColors[item.status] || '#898989')}>{item.status}</Tag>
        </Field>
        <Field label="Billing Cycle">{item.billingCycle ?? '—'}</Field>
        <Field label="Provider">
          <Tag style={tagStyle(providerColors[item.provider] || '#898989')}>{item.provider}</Tag>
        </Field>
        {item.providerCustomerId && <Field label="Provider Customer ID">{item.providerCustomerId}</Field>}
        {item.providerSubId && <Field label="Provider Subscription ID">{item.providerSubId}</Field>}
        <Field label="Period Start">{new Date(item.currentPeriodStart).toLocaleString()}</Field>
        <Field label="Period End">{new Date(item.currentPeriodEnd).toLocaleString()}</Field>
        {item.trialEnd && <Field label="Trial End">{new Date(item.trialEnd).toLocaleString()}</Field>}
        {item.canceledAt && <Field label="Canceled At">{new Date(item.canceledAt).toLocaleString()}</Field>}
        {item.expiresAt && <Field label="Expires At">{new Date(item.expiresAt).toLocaleString()}</Field>}
        <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
        <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
      </div>
    </Show>
  );
};
